import React from 'react';
import { Share2, Facebook, Twitter, MessageCircle, Copy, Check, Send } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';

/**
 * SocialShare - Component for sharing content on social networks
 * Supports both web and native (APK) platforms
 */
const SocialShare = ({ 
  title = "Fleur de Vie", 
  customMessage = null,
  url = null,
  compact = false,
  className = ""
}) => {
  const [copied, setCopied] = React.useState(false);
  
  // Check if running as native app
  const isNative = Capacitor.isNativePlatform();
  
  // Default engagement message
  const defaultMessage = `🌸 Je découvre Fleur de Vie, l'application qui révolutionne l'éducation à la santé reproductive! Rejoignez-moi et cultivez votre fleur personnelle tout en apprenant. 100% gratuit et bienveillant. 💪✨ #FleurDeVie #SantéFéminine #Éducation`;
  
  const message = customMessage || defaultMessage;
  const shareUrl = url || 'https://fleurde-vie.com';
  
  const encodedMessage = encodeURIComponent(message);
  const encodedUrl = encodeURIComponent(shareUrl);
  
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedMessage}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedMessage}&url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedMessage}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedMessage}`,
  };

  // Open link - uses Browser plugin for native, window.open for web
  const openLink = async (url) => {
    try {
      if (isNative) {
        await Browser.open({ url });
      } else {
        window.open(url, '_blank', 'width=600,height=400,scrollbars=yes');
      }
    } catch (error) {
      console.error('Error opening link:', error);
      // Fallback: try to open directly
      window.location.href = url;
    }
  };

  const handleShare = async (platform) => {
    const link = shareLinks[platform];
    if (link) {
      await openLink(link);
    }
  };

  const handleCopyLink = async () => {
    try {
      const textToCopy = `${message}\n\n${shareUrl}`;
      
      if (isNative) {
        // For native, use Share plugin with just text
        await Share.share({
          text: textToCopy,
        });
      } else {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast({
          title: 'Copié!',
          description: 'Le message et le lien ont été copiés dans le presse-papier'
        });
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de copier le lien',
        variant: 'destructive'
      });
    }
  };

  // Native share using Capacitor Share plugin
  const handleNativeShare = async () => {
    try {
      if (isNative) {
        // Use Capacitor Share plugin for native
        await Share.share({
          title: title,
          text: message,
          url: shareUrl,
          dialogTitle: 'Partager Fleur de Vie',
        });
      } else if (navigator.share) {
        // Use Web Share API for browsers that support it
        await navigator.share({
          title: title,
          text: message,
          url: shareUrl,
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
      }
    }
  };

  // Check if native share is available
  const canNativeShare = isNative || (typeof navigator !== 'undefined' && navigator.share);

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('facebook')}
          className="p-2 bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="share-facebook-compact"
        >
          <Facebook className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('twitter')}
          className="p-2 bg-sky-500 hover:bg-sky-600 text-white"
          data-testid="share-twitter-compact"
        >
          <Twitter className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('whatsapp')}
          className="p-2 bg-green-600 hover:bg-green-700 text-white"
          data-testid="share-whatsapp-compact"
        >
          <MessageCircle className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleShare('telegram')}
          className="p-2 bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="share-telegram-compact"
        >
          <Send className="w-4 h-4" />
        </Button>
        {canNativeShare && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNativeShare}
            className="p-2 bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="share-native-compact"
          >
            <Share2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`} data-testid="social-share-container">
      {/* Preview Message */}
      <div className="p-3 bg-white/5 rounded-lg border border-white/10">
        <p className="text-pink-100 text-sm">{message}</p>
      </div>
      
      {/* Share Buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Button 
          onClick={() => handleShare('facebook')}
          className="bg-blue-600 hover:bg-blue-700 text-white border-0"
          data-testid="share-facebook"
        >
          <Facebook className="w-4 h-4 mr-2" />
          Facebook
        </Button>
        <Button 
          onClick={() => handleShare('twitter')}
          className="bg-sky-500 hover:bg-sky-600 text-white border-0"
          data-testid="share-twitter"
        >
          <Twitter className="w-4 h-4 mr-2" />
          Twitter
        </Button>
        <Button 
          onClick={() => handleShare('whatsapp')}
          className="bg-green-600 hover:bg-green-700 text-white border-0"
          data-testid="share-whatsapp"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>
        <Button 
          onClick={() => handleShare('telegram')}
          className="bg-blue-500 hover:bg-blue-600 text-white border-0"
          data-testid="share-telegram"
        >
          <Send className="w-4 h-4 mr-2" />
          Telegram
        </Button>
      </div>

      {/* Copy & Native Share Row */}
      <div className="grid grid-cols-2 gap-3">
        <Button 
          onClick={handleCopyLink}
          className="bg-gray-600 hover:bg-gray-700 text-white border-0"
          data-testid="share-copy"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Copié!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4 mr-2" />
              Copier
            </>
          )}
        </Button>
        
        {canNativeShare && (
          <Button 
            onClick={handleNativeShare}
            className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
            data-testid="share-native"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Plus d'options
          </Button>
        )}
      </div>
    </div>
  );
};

export default SocialShare;
